import { useEffect, useRef, useState } from 'react';
import useAuthV3 from './hooks/useAuthV3';
import { LoadingWand } from '@ordr-as/umami-react';
import useRedirectUrl from '../../hooks/useRedirectUrl';
import useRedirectBack from '../../hooks/useRedirectBack';
import useIOS from './hooks/useIOS';
import Unreachable from '../../Unreachable';

const RedirectLoggedInUser = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const userInputRedirectUrl = urlParams.get('redirect_url');
  const clientId = urlParams.get('client_id');
  const nonce = urlParams.get('nonce');
  const locationIdString =
    urlParams.get('location_id') || localStorage.getItem('location_id');
  const locationId = locationIdString ? parseInt(locationIdString) : undefined;
  const { iosAppVersion, iosReady } = useIOS();

  const { redirectUrl, redirectUrlError } = useRedirectUrl(
    userInputRedirectUrl,
    clientId
  );
  const [unreachable, setUnreachable] = useState(false);

  const redirectBack = useRedirectBack(redirectUrl, nonce);

  const onLoginFailed = () => {
    if (locationId) {
      urlParams.set('location_id', locationId.toString());
      window.location.replace(`/v3/pin?${urlParams.toString()}`);
      return;
    }

    window.location.replace(`/v3/login?${urlParams.toString()}`);
  };

  const { checkTokenInLocalStorage } = useAuthV3({
    onSuccess: redirectBack,
    onFailure: onLoginFailed,
  });
  const tokenCheckStarted = useRef(false);
  const unmounted = useRef(false);
  useEffect(
    () => () => {
      unmounted.current = true;
    },
    []
  );

  // A refused redirect URL used to leave this on its spinner for good, since nothing acted on the
  // error. Sending it down the failure path puts an error screen in front of whoever is there.
  useEffect(() => {
    if (redirectUrlError) {
      onLoginFailed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectUrlError]);

  // Exactly once: refreshing twice with the same token can invalidate it. The check retries on its
  // own while the server is unreachable, so it is only cancelled on unmount, not on re-render.
  useEffect(() => {
    if (
      tokenCheckStarted.current ||
      redirectUrlError ||
      (iosAppVersion && !iosReady)
    ) {
      return;
    }

    tokenCheckStarted.current = true;
    checkTokenInLocalStorage({
      onUnreachable: () => setUnreachable(true),
      isCancelled: () => unmounted.current,
    });
  }, [checkTokenInLocalStorage, redirectUrlError, iosAppVersion, iosReady]);

  return (
    <div className="flex min-h-screen flex-col gap-4 justify-center items-center">
      <LoadingWand />
      {unreachable && <Unreachable />}
    </div>
  );
};

export default RedirectLoggedInUser;
